import type { JSX } from 'react';
import { Node, mergeAttributes, ReactNodeViewRenderer, NodeViewWrapper, type Editor, type NodeViewProps } from '@tiptap/react';
import { useState } from 'react';
import { PictureOutlined, PlayCircleOutlined, CloudUploadOutlined, LinkOutlined, CopyOutlined, LoadingOutlined } from '@ant-design/icons';

import type { SlashUploadFn } from './slash-commands';

/**
 * MediaEmbed — блок-плейсхолдер изображения/видео для инлайн-редактора.
 *
 * Поведение (как просил юзер):
 *  - «Изображение»/«Видео» (тулбар или «/») вставляют ПУСТОЙ блок-плейсхолдер.
 *  - Клик по блоку раскрывает выбор источника: с компьютера / по URL / из
 *    буфера обмена.
 *  - После выбора плейсхолдер ЗАМЕНЯЕТСЯ на реальный контент:
 *      image → нода `image` (Tiptap) → markdown `![](url)`
 *      video (загрузка к нам) → нода `fkVideo` (наш S3, ```video в .md);
 *      video по внешнему URL → абзац `🎬 [подпись](url)`
 *  - Пустой неиспользованный плейсхолдер в markdown не сериализуется
 *    (renderHTML пустой) — мусора в .md не остаётся.
 *
 * uploadFile берётся из опций расширения (передаётся из TiptapEditor).
 */

export interface MediaEmbedOptions {
  uploadFile?: SlashUploadFn;
}

/** Атрибуты для вставки пустого блока-плейсхолдера. */
export function mediaEmbedAttrs(kind: 'image' | 'video'): { type: 'mediaEmbed'; attrs: { kind: string; src: null; name: string } } {
  return { type: 'mediaEmbed', attrs: { kind, src: null, name: '' } };
}

export const MediaEmbed = Node.create<MediaEmbedOptions>({
  name: 'mediaEmbed',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return { uploadFile: undefined };
  },

  addAttributes() {
    return {
      kind: { default: 'image' },
      src: { default: null },
      name: { default: '' },
    };
  },

  // Пустой плейсхолдер не имеет смыслового HTML → при сериализации исчезает.
  parseHTML() {
    return [{ tag: 'div[data-media-embed]' }];
  },
  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-media-embed': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MediaEmbedView);
  },
});

/** Открыть нативный диалог выбора файла. */
function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';
    input.onchange = () => {
      resolve(input.files?.[0] ?? null);
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  });
}

/** Заменить узел-плейсхолдер на реальный контент. */
function fillMedia(editor: Editor, getPos: () => number, kind: string, src: string, name: string, attachmentId?: string): void {
  const pos = getPos();
  const node = editor.state.doc.nodeAt(pos);
  if (!node) return;
  const range = { from: pos, to: pos + node.nodeSize };
  if (kind === 'video' && attachmentId) {
    // Загруженное к нам видео → блок fkVideo (наш S3 + свой плеер, фаза 0.2).
    const caption = (name || '').replace(/\.[a-z0-9]{2,5}$/i, '');
    editor.chain().focus().insertContentAt(range, { type: 'fkVideo', attrs: { id: attachmentId, caption } }).run();
    return;
  }
  if (kind === 'image') {
    editor.chain().focus().insertContentAt(range, { type: 'image', attrs: { src, alt: name || '' } }).run();
  } else {
    // Видео: абзац со ссылкой 🎬 (сериализуется как `🎬 [подпись](url)`).
    const label = (name || 'Смотреть видео').replace(/[<>]/g, '');
    editor.chain().focus().insertContentAt(range, {
      type: 'paragraph',
      content: [
        { type: 'text', text: '🎬 ' },
        { type: 'text', marks: [{ type: 'link', attrs: { href: src } }], text: label },
      ],
    }).run();
  }
}

function MediaEmbedView(props: NodeViewProps): JSX.Element {
  const { node, editor, getPos, deleteNode, extension } = props;
  const kind: 'image' | 'video' = node.attrs.kind === 'video' ? 'video' : 'image';
  const uploadFile: SlashUploadFn | undefined = (extension.options as MediaEmbedOptions).uploadFile;

  const [mode, setMode] = useState<'choose' | 'url'>('choose');
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const Icon = kind === 'video' ? PlayCircleOutlined : PictureOutlined;
  const title = kind === 'video' ? 'Добавить видео' : 'Добавить изображение';
  const accept = kind === 'video' ? 'video/*' : 'image/*';

  const doUpload = async (file: File): Promise<void> => {
    if (!uploadFile) { setErr('Сначала сохраните статью, потом загружайте файлы.'); return; }
    setBusy(true); setErr(null);
    const r = await uploadFile(file).catch(() => null);
    setBusy(false);
    if (r) fillMedia(editor, getPos, kind, r.url, r.fileName, r.id);
    else setErr('Не удалось загрузить файл.');
  };

  const fromComputer = async (): Promise<void> => {
    const file = await pickFile(accept);
    if (file) void doUpload(file);
  };

  const fromUrl = (): void => {
    const u = url.trim();
    if (u) fillMedia(editor, getPos, kind, u, '');
  };

  const fromClipboard = async (): Promise<void> => {
    setErr(null);
    try {
      // 1) Пытаемся достать картинку из буфера (только image-блоки).
      if (kind === 'image' && navigator.clipboard && 'read' in navigator.clipboard) {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const imgType = item.types.find((t) => t.startsWith('image/'));
          if (imgType) {
            const blob = await item.getType(imgType);
            const ext = imgType.split('/')[1] || 'png';
            void doUpload(new File([blob], `clipboard.${ext}`, { type: imgType }));
            return;
          }
        }
      }
      // 2) Иначе — текст из буфера как URL.
      const text = (await navigator.clipboard.readText()).trim();
      if (/^https?:\/\//i.test(text)) {
        fillMedia(editor, getPos, kind, text, '');
        return;
      }
      setErr('В буфере нет картинки или ссылки.');
    } catch {
      setErr('Нет доступа к буферу обмена — вставьте URL вручную.');
    }
  };

  return (
    <NodeViewWrapper className="fk-media-embed" data-kind={kind}>
      <div
        contentEditable={false}
        style={{
          border: '1.5px dashed var(--fk-border-strong)', borderRadius: 12, padding: 18,
          background: 'linear-gradient(160deg,rgba(255,105,0,.05),rgba(0,150,255,.04))',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, position: 'relative',
        }}
      >
        {/* удалить блок */}
        <button type="button" onClick={() => deleteNode()} title="Удалить блок"
          style={{ position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: 6, border: '1px solid var(--fk-border)', background: 'var(--fk-surface)', color: 'var(--fk-text-muted)', cursor: 'pointer', lineHeight: 1, fontSize: 12 }}>×</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon style={{ fontSize: 22, color: 'var(--fk-orange)' }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--fk-text)' }}>{title}</span>
        </div>

        {mode === 'choose' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            <button type="button" className="fk-me-btn" disabled={busy} onClick={() => void fromComputer()}>
              {busy ? <LoadingOutlined spin /> : <CloudUploadOutlined />} С компьютера
            </button>
            <button type="button" className="fk-me-btn ghost" onClick={() => setMode('url')}>
              <LinkOutlined /> Вставить URL
            </button>
            <button type="button" className="fk-me-btn ghost" disabled={busy} onClick={() => void fromClipboard()}>
              <CopyOutlined /> Из буфера
            </button>
          </div>
        ) : (
          <div style={{ width: '100%', maxWidth: 480, display: 'flex', gap: 8 }}>
            <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fromUrl(); } }}
              placeholder={kind === 'video' ? 'https://youtu.be/… / rutube / vk' : 'https://…/картинка.png'}
              className="fk-me-input" />
            <button type="button" className="fk-me-btn" disabled={!url.trim()} onClick={fromUrl}>Вставить</button>
            <button type="button" className="fk-me-btn ghost" onClick={() => { setMode('choose'); setUrl(''); }}>Отмена</button>
          </div>
        )}

        {err && <div style={{ fontSize: 12, color: 'var(--fk-error)' }}>{err}</div>}
      </div>
      <style>{`
        .fk-media-embed{margin:14px 0}
        .fk-me-btn{display:inline-flex;align-items:center;gap:7px;padding:9px 15px;border-radius:10px;border:1px solid var(--fk-orange);background:var(--fk-orange);color:#fff;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;transition:filter .1s}
        .fk-me-btn:hover{filter:brightness(1.05)}
        .fk-me-btn:disabled{opacity:.5;cursor:not-allowed}
        .fk-me-btn.ghost{background:var(--fk-surface);color:var(--fk-text-2);border-color:var(--fk-border)}
        .fk-me-btn.ghost:hover{border-color:var(--fk-orange);color:var(--fk-orange);filter:none}
        .fk-me-input{flex:1;border:1px solid var(--fk-border);border-radius:9px;padding:9px 12px;font-family:inherit;font-size:13.5px;color:var(--fk-text);background:var(--fk-surface);outline:none}
        .fk-me-input:focus{border-color:var(--fk-orange)}
      `}</style>
    </NodeViewWrapper>
  );
}
